/**
 * embedTokenBridge — the P0-hardened bootstrap layer for INZARA's
 * iframe embed pages.
 *
 * Legacy behavior (v0.1 SDK / iter-192): the parent SDK loaded the
 * iframe at `/embed/v2/insights?token=<jwt>` — putting the JWT in the
 * URL query string. That leaks the token via `document.referrer`,
 * browser history, server access logs, and any well-meaning "share
 * link" affordance in the host.
 *
 * v0.2 SDK / iter-193 rotates tokens silently but the URL still
 * contains the *initial* JWT. This module closes that gap by turning
 * the iframe into a `postMessage`-only recipient:
 *
 *   1. The SDK loads the iframe at `/embed/v2/bootstrap?target=<...>`
 *      — the URL carries only the target resource (safe, non-secret).
 *   2. The provider mounts, sends `{source:'inzara.embed',
 *      type:'bootstrap.ready'}` to the parent, and waits.
 *   3. The SDK replies with `{source:'inzara.host',
 *      type:'token.bootstrap', token}`.
 *   4. Once the token lands in-memory, the bootstrap page navigates
 *      client-side (no browser reload) to the real embed page.
 *   5. Subsequent silent refreshes reuse the existing `token.updated`
 *      channel from v0.2.
 *
 * If no bootstrap message arrives within ~5 s, the provider falls
 * back to `?token=` in the URL for backward compatibility with hosts
 * still pinned to the v0.1 SDK.
 */
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const HOST_SOURCE = 'inzara.host';
const EMBED_SOURCE = 'inzara.embed';
const BOOTSTRAP_FALLBACK_MS = 5000;

// Decode a JWT payload (no verification — the server signs & verifies;
// we only read the non-secret `allowed_origins` claim to fail fast in the UI).
function decodeClaims(jwt) {
  try {
    const part = jwt.split('.')[1];
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json);
  } catch { return null; }
}

function originOf(url) {
  try { return new URL(url).origin; } catch { return ''; }
}

const EmbedTokenCtx = createContext({ token: '', source: 'pending' });

/**
 * Wraps every /embed/v2/* route so the pages read their JWT from
 * postMessage first, URL second. Sitting above the router keeps the
 * token stable across in-iframe navigations (bootstrap → real page).
 */
export function EmbedTokenProvider({ children }) {
  const [params] = useSearchParams();
  const urlToken = params.get('token') || '';

  // `source`:
  //   'pending'   — no token yet; the bootstrap page should wait
  //   'postmessage' — token arrived via the parent SDK (v0.2 flow)
  //   'url'       — token pulled from ?token= (legacy fallback)
  const [token, setToken]   = useState(urlToken);
  const [source, setSource] = useState(urlToken ? 'url' : 'pending');
  const [blocked, setBlocked] = useState(null);
  const senderOriginRef = useRef('');
  const notifiedRef = useRef(false);

  // Announce readiness to the parent SDK exactly once per mount.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (notifiedRef.current) return;
    notifiedRef.current = true;
    try {
      window.parent?.postMessage(
        { source: EMBED_SOURCE, type: 'bootstrap.ready' },
        '*',
      );
    } catch { /* iframe messaging blocked → we'll still function via URL fallback */ }
  }, []);

  // Listen for host → embed messages: token.bootstrap and token.updated.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    function onMessage(ev) {
      const data = ev?.data;
      if (!data || typeof data !== 'object') return;
      if (data.source !== HOST_SOURCE) return;
      if (data.type !== 'token.bootstrap' && data.type !== 'token.updated') return;
      if (typeof data.token !== 'string' || !data.token) return;
      senderOriginRef.current = ev.origin || '';
      setToken(data.token);
      setSource('postmessage');
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // Legacy fallback: if the host never posts a bootstrap token within
  // BOOTSTRAP_FALLBACK_MS, honor whatever was in `?token=` at mount so
  // v0.1 hosts don't break.
  useEffect(() => {
    if (source !== 'pending') return undefined;
    if (!urlToken) return undefined;
    const t = setTimeout(() => {
      setToken((cur) => (cur ? cur : urlToken));
      setSource((cur) => (cur === 'pending' ? 'url' : cur));
    }, BOOTSTRAP_FALLBACK_MS);
    return () => clearTimeout(t);
  }, [source, urlToken]);

  // If the URL token itself was present at mount, we treat it as
  // authoritative immediately (v0.1 host). v0.2 hosts hit the bootstrap
  // route with NO token, so `source` starts as 'pending'.
  // Origin allow-listing: if the signed token carries a non-empty
  // `allowed_origins` claim, only render when the embedding site's origin
  // is on the list. The postMessage sender origin (ev.origin) is set by the
  // browser and cannot be forged; for the legacy ?token= path we fall back
  // to document.referrer. An empty allowlist means "embeddable anywhere".
  useEffect(() => {
    if (!token) { setBlocked(null); return; }
    const claims = decodeClaims(token);
    const allowed = (claims && Array.isArray(claims.allowed_origins)) ? claims.allowed_origins : [];
    if (allowed.length === 0) { setBlocked(null); return; }
    const embedderOrigin = senderOriginRef.current
      || (typeof document !== 'undefined' && document.referrer ? originOf(document.referrer) : '');
    // Same-origin (direct open / our own preview) is always allowed.
    const selfOrigin = typeof window !== 'undefined' ? window.location.origin : '';
    if (embedderOrigin === selfOrigin) { setBlocked(null); return; }
    if (embedderOrigin && allowed.includes(embedderOrigin)) { setBlocked(null); return; }
    setBlocked(embedderOrigin || 'unknown origin');
  }, [token]);

  const value = useMemo(() => ({ token, source }), [token, source]);
  if (blocked) {
    return (
      <div data-testid="embed-origin-blocked" style={{ padding: 24, fontFamily: 'system-ui, sans-serif', color: '#b91c1c', fontSize: 14, lineHeight: 1.5 }}>
        <strong>Embed not authorized for this site.</strong>
        <div style={{ color: '#6b7280', marginTop: 6 }}>
          This workspace only allows embedding from approved origins
          {blocked !== 'unknown origin' ? <> (blocked: <code>{blocked}</code>)</> : null}.
          Add this site to the workspace&rsquo;s allowed embed origins to continue.
        </div>
      </div>
    );
  }
  return <EmbedTokenCtx.Provider value={value}>{children}</EmbedTokenCtx.Provider>;
}

/**
 * Preferred way to read the current embed JWT inside any /embed/v2/*
 * page. Returns the fresh token — swapping automatically when the host
 * SDK rotates it. When called outside a Provider (unit tests, story-
 * book) it degrades to reading `?token=` off the current URL so nobody
 * has to wrap tests in the provider.
 */
export function useEmbedToken() {
  const ctx = useContext(EmbedTokenCtx);
  const [params] = useSearchParams();
  if (!ctx || ctx === EmbedTokenCtx._defaultValue) {
    return params.get('token') || '';
  }
  return ctx.token;
}

/**
 * `useEmbedTokenState` returns the token AND its provenance so the
 * bootstrap page can differentiate "still waiting" from "we have a
 * token, redirect now".
 */
export function useEmbedTokenState() {
  return useContext(EmbedTokenCtx);
}
